import { ref, uploadBytes, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { storage } from '../firebase';
import type { ImageMetadata } from '../types/images';

export class ImageService {
  private static instance: ImageService;

  static getInstance(): ImageService {
    if (!ImageService.instance) {
      ImageService.instance = new ImageService();
    }
    return ImageService.instance;
  }

  // Map folder names to actual storage paths
  // Returns { path: string, isRoot: boolean }
  private getFolderPath(folder: string): { path: string; isRoot: boolean } {
    const folderMap: Record<string, { path: string; isRoot: boolean }> = {
      'audio-guides': { path: 'lesson_thumbnails', isRoot: true },
      // Add other mappings as needed
    };
    return folderMap[folder] || { path: folder, isRoot: false };
  }

  // Upload image to specified folder within learning_images
  async uploadImage(file: File, folder: string, fileName?: string): Promise<string> {
    try {
      // Generate unique filename if not provided
      const finalFileName = fileName || `${Date.now()}-${file.name}`;

      // Map folder to actual storage path
      const { path: storagePath, isRoot } = this.getFolderPath(folder);

      // Create reference - use root path or learning_images subfolder
      const fullPath = isRoot 
        ? `${storagePath}/${finalFileName}`
        : `learning_images/${storagePath}/${finalFileName}`;
      const imageRef = ref(storage, fullPath);

      // Upload file
      const snapshot = await uploadBytes(imageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image', { cause: error });
    }
  }

  // Delete image by URL
  async deleteImage(url: string): Promise<void> {
    try {
      // Extract path from URL to create reference
      const imageRef = ref(storage, url);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw new Error('Failed to delete image', { cause: error });
    }
  }

  // List all images in a specific folder
  async listImages(folder?: string): Promise<ImageMetadata[]> {
    try {
      let folderPath: string;
      if (folder) {
        const { path: storagePath, isRoot } = this.getFolderPath(folder);
        folderPath = isRoot ? storagePath : `learning_images/${storagePath}`;
      } else {
        folderPath = 'learning_images';
      }
      const folderRef = ref(storage, folderPath);

      const result = await listAll(folderRef);
      const images: ImageMetadata[] = [];

      // Get metadata and download URL for each image
      for (const itemRef of result.items) {
        try {
          const [metadata, url] = await Promise.all([getMetadata(itemRef), getDownloadURL(itemRef)]);

          images.push({
            url,
            name: itemRef.name,
            size: metadata.size,
            uploadedAt: new Date(metadata.timeCreated),
            folder: itemRef.parent?.name || 'root',
          });
        } catch (err) {
          console.warn(`Failed to get metadata for ${itemRef.name}:`, err);
        }
      }

      // Sort by upload date (newest first)
      return images.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
    } catch (error) {
      console.error('Error listing images:', error);
      throw new Error('Failed to list images', { cause: error });
    }
  }

  // Get all images across all folders
  async listAllImages(): Promise<ImageMetadata[]> {
    try {
      const rootRef = ref(storage, 'learning_images');
      const result = await listAll(rootRef);
      const allImages: ImageMetadata[] = [];

      // Get images from root
      for (const itemRef of result.items) {
        try {
          const [metadata, url] = await Promise.all([getMetadata(itemRef), getDownloadURL(itemRef)]);

          allImages.push({
            url,
            name: itemRef.name,
            size: metadata.size,
            uploadedAt: new Date(metadata.timeCreated),
            folder: 'general',
          });
        } catch (err) {
          console.warn(`Failed to get metadata for ${itemRef.name}:`, err);
        }
      }

      // Get images from subfolders
      for (const folderRef of result.prefixes) {
        try {
          const folderImages = await this.listImages(folderRef.name);
          allImages.push(...folderImages);
        } catch (err) {
          console.warn(`Failed to get images from folder ${folderRef.name}:`, err);
        }
      }

      // Sort by upload date (newest first)
      return allImages.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
    } catch (error) {
      console.error('Error listing all images:', error);
      throw new Error('Failed to list all images', { cause: error });
    }
  }

  // Validate file before upload
  validateFile(file: File): { isValid: boolean; error?: string } {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Invalid file type. Please use JPG, PNG, WebP, or SVG files.',
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File too large. Maximum size is 5MB.',
      };
    }

    return { isValid: true };
  }
}

export const imageService = ImageService.getInstance();
